import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Package,
  GitBranch,
  History,
  Truck,
  Filter,
  Eye,
  QrCode,
  ArrowRight,
  Calendar,
  Factory,
  AlertTriangle,
} from "lucide-react";
import { TraceabilityDetails } from "@/components/Traceability/TraceabilityDetails";
import { ShipmentsView } from "@/components/Traceability/ShipmentsView";
import { QRScanner } from "@/components/QR/QRScanner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface ProductionBatch {
  id: string;
  batch_number: string;
  work_order_id: string | null;
  recipe_id: string | null;
  mold_id: string | null;
  machine_id: string | null;
  quantity: number;
  status: string;
  start_date: string | null;
  end_date: string | null;
  notes: string | null;
  created_at: string;
  work_orders?: {
    order_number: string;
    product_name: string;
    customer: string | null;
  };
  molds?: {
    code: string;
    name: string;
  };
  machines?: {
    name: string;
  };
}

export default function Traceability() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("batches");
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);

  const { data: batches, isLoading } = useQuery({
    queryKey: ["production-batches"],
    queryFn: async () => {
      const { data, error } = await api
        .from("production_batches")
        .select(`
          *,
          work_orders(order_number, product_name, customer),
          molds(code, name),
          machines(name)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ProductionBatch[];
    },
  });

  const filteredBatches = batches?.filter((batch) => {
    const matchesSearch =
      searchTerm === "" ||
      batch.batch_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      batch.work_orders?.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      batch.work_orders?.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      batch.work_orders?.customer?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || batch.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: batches?.length || 0,
    inProgress: batches?.filter((b) => b.status === "in_progress").length || 0,
    completed: batches?.filter((b) => b.status === "completed").length || 0,
    pending: batches?.filter((b) => b.status === "pending").length || 0,
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; color: string }> = {
      pending: { label: "Pendiente", color: "bg-gray-500/10 text-gray-500" },
      in_progress: { label: "En Proceso", color: "bg-blue-500/10 text-blue-500" },
      completed: { label: "Completado", color: "bg-green-500/10 text-green-500" },
      cancelled: { label: "Cancelado", color: "bg-red-500/10 text-red-500" },
      on_hold: { label: "En Espera", color: "bg-yellow-500/10 text-yellow-500" },
    };
    const { label, color } = config[status] || config.pending;
    return <Badge className={color}>{label}</Badge>;
  };

  if (selectedBatchId) {
    return (
      <DashboardLayout>
        <TraceabilityDetails
          batchId={selectedBatchId}
          onBack={() => setSelectedBatchId(null)}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Trazabilidad</h1>
            <p className="text-muted-foreground mt-1">
              Seguimiento completo de lotes, materiales y productos
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowScanner(true)}>
              <QrCode className="w-4 h-4 mr-2" />
              Escanear Codigo
            </Button>
          </div>
        </div>

        {showScanner && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowScanner(false)}>
            <div onClick={(e) => e.stopPropagation()}>
              <QRScanner
                onScan={(data: any) => {
                  const code = typeof data === "string" ? data : (data?.batch_number || data?.code || data?.id || data?.lot || data?.pn_code || JSON.stringify(data));
                  setSearchTerm(String(code));
                  setShowScanner(false);
                }}
                onClose={() => setShowScanner(false)}
              />
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="batches" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Lotes
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Timeline
            </TabsTrigger>
            <TabsTrigger value="shipments" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Envios
            </TabsTrigger>
          </TabsList>

          <TabsContent value="batches" className="mt-6 space-y-6">
            {/* Stats */}
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <Package className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.total}</p>
                      <p className="text-sm text-muted-foreground">Total Lotes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-yellow-500/10">
                      <Factory className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-yellow-600">{stats.inProgress}</p>
                      <p className="text-sm text-muted-foreground">En Proceso</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <Package className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                      <p className="text-sm text-muted-foreground">Completados</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gray-500/10">
                      <GitBranch className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.pending}</p>
                      <p className="text-sm text-muted-foreground">Pendientes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[250px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por lote, OT, producto o cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="in_progress">En Proceso</SelectItem>
                  <SelectItem value="completed">Completados</SelectItem>
                  <SelectItem value="cancelled">Cancelados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Batches List */}
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              </div>
            ) : filteredBatches && filteredBatches.length > 0 ? (
              <div className="space-y-3">
                {filteredBatches.map((batch) => (
                  <Card
                    key={batch.id}
                    className={cn(
                      "cursor-pointer hover:shadow-lg transition-all",
                      batch.status === "in_progress" && "border-l-4 border-l-blue-500",
                      batch.status === "completed" && "border-l-4 border-l-green-500"
                    )}
                    onClick={() => setSelectedBatchId(batch.id)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-lg bg-primary/10">
                            <Package className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="font-bold text-lg">{batch.batch_number}</h3>
                              {getStatusBadge(batch.status)}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {batch.work_orders?.product_name || "Sin producto"}
                              {batch.work_orders?.order_number && (
                                <span className="ml-2">| OT: {batch.work_orders.order_number}</span>
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-right hidden md:block">
                            <p className="text-sm text-muted-foreground">Cantidad</p>
                            <p className="font-bold">{batch.quantity} uds</p>
                          </div>
                          {batch.molds && (
                            <div className="text-right hidden lg:block">
                              <p className="text-sm text-muted-foreground">Molde</p>
                              <p className="font-medium">{batch.molds.code}</p>
                            </div>
                          )}
                          {batch.machines && (
                            <div className="text-right hidden lg:block">
                              <p className="text-sm text-muted-foreground">Maquina</p>
                              <p className="font-medium">{batch.machines.name}</p>
                            </div>
                          )}
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Fecha</p>
                            <p className="font-medium">
                              {format(new Date(batch.created_at), "dd/MM/yyyy", { locale: es })}
                            </p>
                          </div>
                          <Button variant="ghost" size="icon">
                            <Eye className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>

                      {batch.work_orders?.customer && (
                        <div className="mt-3 pt-3 border-t flex items-center gap-2 text-sm text-muted-foreground">
                          <span>Cliente: {batch.work_orders.customer}</span>
                          {batch.start_date && (
                            <>
                              <ArrowRight className="w-4 h-4" />
                              <Calendar className="w-4 h-4" />
                              <span>
                                Inicio: {format(new Date(batch.start_date), "dd/MM/yyyy HH:mm", { locale: es })}
                              </span>
                            </>
                          )}
                          {batch.end_date && (
                            <>
                              <ArrowRight className="w-4 h-4" />
                              <span>
                                Fin: {format(new Date(batch.end_date), "dd/MM/yyyy HH:mm", { locale: es })}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Package className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {searchTerm || statusFilter !== "all"
                      ? "No se encontraron lotes con los filtros seleccionados"
                      : "No hay lotes de produccion registrados"}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="timeline" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Timeline Global de Eventos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <GlobalTimeline />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="shipments" className="mt-6">
            <ShipmentsView />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

// Componente de Timeline Global
function GlobalTimeline() {
  const { data: events, isLoading } = useQuery({
    queryKey: ["traceability-events-global"],
    queryFn: async () => {
      const { data, error } = await api
        .from("traceability_events")
        .select(`
          *,
          production_batches(batch_number),
          work_orders(order_number, product_name),
          profiles:performed_by(full_name, email)
        `)
        .order("event_date", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
  });

  const getEventIcon = (type: string) => {
    const icons: Record<string, any> = {
      created: Package,
      material_added: GitBranch,
      process_started: Factory,
      process_completed: Package,
      process_alert: AlertTriangle,
      quality_check: Search,
      shipped: Truck,
    };
    const Icon = icons[type] || History;
    return <Icon className="w-4 h-4" />;
  };

  const getEventColor = (type: string) => {
    const colors: Record<string, string> = {
      created: "bg-blue-500",
      material_added: "bg-purple-500",
      process_started: "bg-yellow-500",
      process_completed: "bg-green-500",
      process_alert: "bg-amber-500",
      quality_check: "bg-orange-500",
      shipped: "bg-cyan-500",
    };
    return colors[type] || "bg-gray-500";
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No hay eventos de trazabilidad registrados</p>
        <p className="text-sm mt-1">Los eventos apareceran aqui cuando se registren movimientos en los lotes</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
      <div className="space-y-4">
        {events.map((event: any) => (
          <div key={event.id} className="relative pl-10">
            <div
              className={cn(
                "absolute left-2 w-5 h-5 rounded-full flex items-center justify-center text-white",
                getEventColor(event.event_type)
              )}
            >
              {getEventIcon(event.event_type)}
            </div>
            <div className="p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{event.event_type}</Badge>
                  {event.production_batches?.batch_number && (
                    <Badge>{event.production_batches.batch_number}</Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  {format(new Date(event.event_date), "dd/MM/yyyy HH:mm", { locale: es })}
                </span>
              </div>
              {event.description && (
                <p className="text-sm mt-2">{event.description}</p>
              )}
              {event.profiles && (
                <p className="text-xs text-muted-foreground mt-1">
                  Por: {event.profiles.full_name || event.profiles.email}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
